import React, { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, CheckCircle2, Car, Wrench, Image as ImageIcon, ListChecks } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import SignaturePad from '../components/SignaturePad';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function AkkoordPage({ token }) {
  const { t, lang, setLang, languages } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [naam, setNaam] = useState('');
  const [sig, setSig] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    carsysApi.getPublicWerkorder(token).then(setData).catch(e => setError(e.response?.status === 404 ? 'notfound' : 'error'));
  }, [token]);

  const submit = async (akkoord) => {
    if (akkoord && !naam.trim()) { alert(t('Vul uw naam in.')); return; }
    if (akkoord && !sig) { alert(t('Plaats uw handtekening.')); return; }
    setBusy(true);
    try {
      await carsysApi.submitWerkorderAkkoord(token, { naam: naam.trim() || 'Klant', handtekening: sig, akkoord });
      setDone(akkoord ? 'akkoord' : 'afgewezen');
    } catch (e) { alert(t('Er ging iets mis:') + ' ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (error === 'notfound') return <Center>🔍 {t('Werkorder niet gevonden of de link is verlopen.')}</Center>;
  if (error) return <Center>{t('Er ging iets mis. Probeer het later opnieuw.')}</Center>;
  if (!data) return <Center><Loader2 className="spin" size={28} /></Center>;

  const reedsAkkoord = data.akkoordStatus === 'akkoord';

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'system-ui, sans-serif', padding: '24px 12px' }} data-testid="akkoord-page">
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div style={{ background: '#0f172a', color: '#fff', borderRadius: '14px 14px 0 0', padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ fontSize: 13, opacity: .8 }}>{data.garage.naam}</div>
            {languages.length > 1 && (
              <select value={lang} onChange={e => setLang(e.target.value)} data-testid="akkoord-lang-select"
                style={{ background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)', borderRadius: 8, color: '#fff', padding: '5px 8px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                {languages.map(l => <option key={l.code} value={l.code} style={{ color: '#000' }}>{(l.code || '').toUpperCase()}</option>)}
              </select>
            )}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}><Wrench size={20} /> {data.type === 'Offerte' ? t('Offerte') : t('Werkorder')} {data.nummer}</div>
          <div style={{ fontSize: 13, opacity: .85, marginTop: 4 }}>{data.kenteken ? <><Car size={13} style={{ verticalAlign: -2 }} /> {data.kenteken} · </> : ''}{data.datum}</div>
        </div>

        <div style={{ background: '#fff', padding: 24, borderRadius: done || reedsAkkoord ? '0 0 14px 14px' : 0 }}>
          {data.omschrijving && <p style={{ marginTop: 0, color: '#334155' }}>{data.omschrijving}</p>}

          <h3 style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}><ListChecks size={15} /> {t('Werkzaamheden')}</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }} data-testid="akkoord-regels">
            <tbody>
              {data.regels.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #eef2f7' }}>
                  <td style={{ padding: '7px 0' }}>{r.code ? <b>{r.code} </b> : ''}{r.omschr}{(r.markering === 'Garantie') ? <i style={{ color: '#92400e' }}> ({t('garantie')})</i> : ''}</td>
                  <td style={{ padding: '7px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.aantal} × {eur(r.markering === 'Garantie' ? 0 : r.prijs)}</td>
                </tr>
              ))}
              {data.regels.length === 0 && <tr><td style={{ color: '#94a3b8', padding: 8 }}>{t('Nog geen regels.')}</td></tr>}
            </tbody>
            <tfoot>
              {data.subtotaal != null && <tr><td style={{ textAlign: 'right', color: '#64748b' }}>{t('Subtotaal')}</td><td style={{ textAlign: 'right' }}>{eur(data.subtotaal)}</td></tr>}
              {data.btw != null && <tr><td style={{ textAlign: 'right', color: '#64748b' }}>{t('BTW')} {data.btwPercentage}%</td><td style={{ textAlign: 'right' }}>{eur(data.btw)}</td></tr>}
              <tr><td style={{ textAlign: 'right', fontWeight: 800, paddingTop: 6 }}>{t('Totaal')}</td><td style={{ textAlign: 'right', fontWeight: 800, paddingTop: 6 }}>{eur(data.totaal)}</td></tr>
            </tfoot>
          </table>

          {(data.fotosVoor?.length > 0 || data.fotosNa?.length > 0) && (
            <div style={{ marginTop: 18 }}>
              <h3 style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}><ImageIcon size={15} /> {t("Foto's")}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[...(data.fotosVoor || []), ...(data.fotosNa || [])].slice(0, 8).map((src, i) => (
                  <img key={i} src={src} alt="" style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                ))}
              </div>
            </div>
          )}

          {data.garage.voorwaarden && <div style={{ marginTop: 16, fontSize: 12, color: '#64748b', background: '#f8fafc', padding: 12, borderRadius: 8, whiteSpace: 'pre-wrap' }}>{data.garage.voorwaarden}</div>}
        </div>

        {(done || reedsAkkoord) ? (
          <div style={{ background: '#f0fdf4', borderRadius: '0 0 14px 14px', padding: 28, textAlign: 'center', color: '#15803d' }} data-testid="akkoord-bedankt">
            <CheckCircle2 size={40} style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 18, fontWeight: 800 }}>{done === 'afgewezen' ? t('Reactie ontvangen') : t('Bedankt voor uw akkoord!')}</div>
            <div style={{ fontSize: 13.5, color: '#475569', marginTop: 4 }}>
              {reedsAkkoord && !done ? `${t('Reeds akkoord gegeven door')} ${data.akkoordNaam || t('klant')}.` : `${data.garage.naam} ${t('is op de hoogte gesteld.')}`}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 14px 14px', padding: 24 }}>
            <h3 style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 6, marginTop: 0 }}><ShieldCheck size={16} color="#2563eb" /> {t('Akkoord geven')}</h3>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b' }}>{t('Uw naam')}</label>
            <input value={naam} onChange={e => setNaam(e.target.value)} data-testid="akkoord-naam"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, marginTop: 4, marginBottom: 12, boxSizing: 'border-box' }} placeholder={t('Voor- en achternaam')} />
            <label style={{ fontSize: 12.5, fontWeight: 600, color: '#64748b' }}>{t('Handtekening')}</label>
            <div style={{ marginTop: 4 }}><SignaturePad onChange={setSig} /></div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={() => submit(true)} disabled={busy} data-testid="akkoord-submit"
                style={{ flex: 1, padding: '13px', border: 'none', borderRadius: 10, background: '#16a34a', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
                {busy ? t('Bezig…') : t('Akkoord & ondertekenen')}
              </button>
              <button onClick={() => submit(false)} disabled={busy} data-testid="akkoord-afwijzen"
                style={{ padding: '13px 18px', border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                {t('Niet akkoord')}
              </button>
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 12, textAlign: 'center' }}>{t('Door te ondertekenen gaat u akkoord met de werkzaamheden. Datum, tijd en apparaatgegevens worden vastgelegd.')}</p>
          </div>
        )}
        <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 14 }}>{t('Mogelijk gemaakt door')} MonteurCentraal</div>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Center({ children }) {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', color: '#64748b', fontFamily: 'system-ui', fontSize: 16, padding: 20, textAlign: 'center' }}>{children}</div>;
}
